const { parseEnvelope, serializeEnvelope } = require('@sentry/core');

// SDK retries can outlive a client. Check ownership inside each transaction,
// before removing an envelope, so retired retries cannot consume a new client's queue.
module.exports = function createOfflineStore(isActive, ready, options) {
    async function insert(envelope, first) {
        const serialized = await serializeEnvelope(envelope);
        await transact((store, keys) => {
            if (keys.length >= (options.maxQueueSize ?? 30)) return;
            const key = first ? Math.min(...keys, 0) - 1 : Math.max(...keys, 0) + 1;
            store.put(serialized, key);
        });
    }
    return {
        push: envelope => insert(envelope, false).catch(() => {}),
        unshift: envelope => insert(envelope, true).catch(() => {}),
        shift: () => transact((store, keys, setResult) => {
            if (!keys.length) return;
            const request = store.get(keys[0]);
            request.onsuccess = () => {
                if (!isActive()) return;
                store.delete(keys[0]);
                setResult(request.result);
            };
        }).then(serialized => serialized ? parseEnvelope(serialized) : undefined).catch(() => undefined)
    };

    async function transact(operation) {
        await ready();
        if (!isActive() || typeof indexedDB === 'undefined') return;
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('sentry-production-only');
            request.onupgradeneeded = () => request.result.createObjectStore('queue');
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const db = request.result;
                if (!isActive()) { db.close(); resolve(undefined); return; }
                const transaction = db.transaction('queue', 'readwrite');
                const store = transaction.objectStore('queue');
                let result;
                transaction.oncomplete = () => { db.close(); resolve(result); };
                transaction.onerror = transaction.onabort = () => { db.close(); reject(transaction.error); };
                const keys = store.getAllKeys();
                keys.onsuccess = () => {
                    if (isActive()) operation(store, keys.result, value => { result = value; });
                };
            };
        });
    }
};
